import chevronLeft from '@getgo/chameleon-icons/dist/rebranded/chevron-left-outlined.svg';
import chevronRight from '@getgo/chameleon-icons/dist/rebranded/chevron-right-outlined.svg';
import { withLocalization } from 'synapse-js/localization/withLocalization';
import { css, html, LitElement, nothing } from 'lit';
import { buttons, spacing, typography } from '../../../styles/index.js';
import 'synapse-js/ui-components/synapse-icon';
import { FIRST } from '../../../constants/pagination.js';

class TablePagination extends withLocalization(LitElement) {
  static get properties() {
    return {
      totalCount: { type: Number, attribute: 'total-count' },
      count: { type: Number, attribute: 'count' },
      page: { type: Number, reflect: true },
      pageSize: { type: Number, attribute: false },
      pageSizeOptions: { type: Array },
      type: { type: String, attribute: 'type' },
      fireLoadMoreEvent: { type: Boolean, attribute: false },
      ongoingChanges: { type: Array, attribute: 'ongoing-changes' },
    };
  }

  static get styles() {
    return [
      buttons,
      spacing,
      typography,
      css`
        .pagination-button {
          all: unset;
          display: grid;
          place-items: center;
          width: 32px;
          aspect-ratio: 1;
        }

        .pagination-button:not(:disabled):hover {
          background-color: var(--interactive-color-secondary-hover);
          border-radius: 16px;
          cursor: pointer;
        }

        select {
          height: 40px;
          width: 60px;
          padding: 4px;
          color: var(--type-color-default);
          background-color: var(--surface-color-default);
          border-color: var(--border-color-default);
          font-size: 16px;
          border-radius: 4px;
          cursor: pointer;
          text-align: center;
        }

        .pagination-container {
          display: flex;
          flex-direction: row;
          gap: 8px;
          align-items: center;
          font-size: 16px;
          margin-left: auto;
        }
      `,
    ];
  }

  constructor() {
    super();

    this.totalCount = 0;
    this.count = 0;
    this.offset = 0;
    this.page = 1;
    this.pageSizeOptions = [5, 10, 25, 50];
    this.pageSize = 10;
    this.type = '';
    this.pageSizeKey = '';
    this.fireLoadMoreEvent = true;
    this.ongoingChanges = new Set();
    this.numberOfDevicesChanges = null;
    this.numberOfRemovedDevices = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.pageSizeKey = `${this.type}-table-page-size`;
    this.pageSize = this.lastSavedPageSize;
    this.dispatchPageSize();
    window.addEventListener('reset-pagination', this.reset.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('reset-pagination', this.reset);
  }

  get lastSavedPageSize() {
    const size = localStorage.getItem(this.pageSizeKey);
    return size ? parseInt(size, 10) : this.pageSizeOptions[0];
  }

  render() {
    if (this.totalCount <= this.pageSizeOptions[0]) {
      return nothing;
    }

    return html`
      <div class="pagination-container">
        ${this.localization('showPerPage')}

        <select @change="${this.onSetPageSize}" id="selector-page-size">
          ${this.pageSizeOptions.map(
            value => html`<option value=${value} ?selected=${value === this.pageSize}>${value}</option>`,
          )}
        </select>

        <button
          @click="${this.onPreviousPage}"
          class="pagination-button"
          id="button-previous-page"
          ?disabled=${this.previousDisabled}
        >
          <synapse-icon .svg="${chevronLeft}" ?disabled=${this.previousDisabled}></synapse-icon>
        </button>

        <div id="indicator-text">${this.localization('page')} ${this.page}/${this.maxPageCount}</div>

        <button
          @click="${this.onNextPage}"
          class="pagination-button"
          id="button-next-page"
          ?disabled=${this.nextDisabled}
        >
          <synapse-icon .svg="${chevronRight}" ?disabled=${this.nextDisabled}></synapse-icon>
        </button>
      </div>
    `;
  }

  updated(changedProperties) {
    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize - 1;

    this.dispatchEvent(new CustomEvent('change', { detail: { start, end } }));

    if (changedProperties.has('totalCount')) {
      this.reset();
    }

    if (changedProperties.has('ongoingChanges') && this.ongoingChanges?.size > 0) {
      this.numberOfDevicesChanges = this.ongoingChanges.size;
    }

    const loadedMoreData = changedProperties.has('count') && changedProperties.get('count') !== this.count;
    if (loadedMoreData) {
      this.fireLoadMoreEvent = true;
      /* Safety check: if the current device count (this.count) is smaller than the
         previous count (changedProperties.get('count')) then it was a remove device operation */
      if (changedProperties.get('count') > this.count) {
        this.numberOfRemovedDevices += 1;
        if (this.numberOfRemovedDevices === this.numberOfDevicesChanges) {
          this.loadMoreDevices();
          this.numberOfDevicesChanges = null;
          this.numberOfRemovedDevices = null;
        }
      }
    }
  }

  /**
   *
   * returns true based on the pageSize
   * for example:
   * pageSize = 5 => returns true on 95%
   * pageSize = 25 => returns true on 75%
   * etc...
   *
   * @returns
   */
  shouldTriggerNextPageRequest() {
    const perc = (this.page / this.currentMaxPageCount) * 100;

    return perc >= 100 - this.pageSize && this.count < this.totalCount;
  }

  get previousDisabled() {
    return this.page <= 1;
  }

  get nextDisabled() {
    return this.page === this.maxPageCount;
  }

  get currentMaxPageCount() {
    return Math.ceil(this.count / this.pageSize);
  }

  get maxPageCount() {
    return Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  reset() {
    this.page = 1;
  }

  onSetPageSize(event) {
    const size = parseInt(event.target.value, 10);
    this.pageSize = size;
    localStorage.setItem(this.pageSizeKey, size.toString());
    this.reset();
    this.dispatchPageSize();
  }

  onPreviousPage() {
    if (this.page === 1) {
      return;
    }

    this.page -= 1;
  }

  onNextPage() {
    if (this.page === this.maxPageCount) {
      return;
    }

    if (this.shouldTriggerNextPageRequest() && this.fireLoadMoreEvent) {
      this.fireLoadMoreEvent = false;
      this.offset += 1;
      this.dispatchEvent(
        new CustomEvent('load-more-data', {
          bubbles: true,
          composed: true,
          detail: { offset: this.offset * FIRST },
        }),
      );
    }

    this.page += 1;
  }

  loadMoreDevices() {
    this.fireLoadMoreEvent = false;
    this.dispatchEvent(
      new CustomEvent('load-more-data', {
        bubbles: true,
        composed: true,
        detail: { offset: this.offset * FIRST },
      }),
    );
  }

  dispatchPageSize() {
    this.dispatchEvent(
      new CustomEvent('page-size-set', { detail: { pageSize: this.pageSize }, composed: true, bubbles: true }),
    );
  }
}

window.customElements.define('table-pagination', TablePagination);
